import './users-balances.scss';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Empty, Input as AntdInput, Table as AntdTable, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ExportOutlined, ReloadOutlined, TeamOutlined } from '@ant-design/icons';
import { Button, buttonType } from 'components/button/button';
import { Card } from 'components/card/card';
import { Page } from 'components/page/page';
import { UsersBalanceAsset } from 'core/domain/wallet/wallet';
import { getAllUsersBalances } from 'core/repositories/wallet';
import { digitSperator } from 'core/utils/decimalExtentions';
import { downloadCSV } from 'core/utils/export-to-file';
import { getIconPath } from 'core/utils/getIconPath';

// ارقام ارزی می‌توانند خیلی کوچک باشند؛ جداکننده‌ی هزارگان روی مقادیر < 1 خوانایی را خراب می‌کند.
const formatQuantity = (value: number): string => {
    if (value === 0) return '0';
    if (Math.abs(value) < 1) return value.toFixed(8).replace(/0+$/, '').replace(/\.$/, '');
    return digitSperator(Number(value.toFixed(4)));
};

export const UsersBalances = () => {
    const [assets, setAssets] = React.useState<UsersBalanceAsset[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(20);
    const navigate = useNavigate();

    const loadData = React.useCallback(() => {
        getAllUsersBalances(setLoading, setAssets);
    }, []);

    React.useEffect(() => {
        loadData();
    }, [loadData]);

    const filteredAssets = React.useMemo(() => {
        const keyword = search.trim().toLowerCase();
        if (!keyword) return assets;
        return assets.filter((a) => a.symbol?.toLowerCase().includes(keyword));
    }, [assets, search]);

    const totalDollarValue = React.useMemo(
        () => filteredAssets.reduce((sum, a) => sum + (a.dollarPrice ?? 0), 0),
        [filteredAssets],
    );

    const columns = React.useMemo<ColumnsType<UsersBalanceAsset>>(
        () => [
            {
                title: '#',
                key: 'index',
                width: 10,
                render: (_, __, index) => index + 1,
            },
            {
                title: 'ارز',
                dataIndex: 'symbol',
                key: 'symbol',
                width: 30,
                render: (symbol: string) => (
                    <div className="symbol-cell">
                        <img src={getIconPath(symbol.toLowerCase())} alt="" />
                        <span className="en">{symbol}</span>
                    </div>
                ),
            },
            {
                title: 'موجودی کل کاربران',
                dataIndex: 'sum',
                key: 'sum',
                align: 'left',
                width: 100,
                sorter: (a, b) => a.sum - b.sum,
                render: (sum: number) => <span className="en">{formatQuantity(sum)}</span>,
            },
            {
                title: 'ارزش دلاری',
                dataIndex: 'dollarPrice',
                key: 'dollarPrice',
                align: 'left',
                width: 100,
                defaultSortOrder: 'descend',
                sorter: (a, b) => (a.dollarPrice ?? 0) - (b.dollarPrice ?? 0),
                render: (dollarPrice: number | null) => (
                    <span className="en">{dollarPrice ? digitSperator(Number(dollarPrice.toFixed(2))) : '0'} $</span>
                ),
            },
            {
                title: 'کاربران',
                key: 'actions',
                width: 100,
                render: (_, row) => (
                    <Tooltip title="مشاهده موجودی کاربران این ارز">
                        <button
                            type="button"
                            className="table-action-button"
                            aria-label="مشاهده موجودی کاربران این ارز"
                            onClick={() => navigate(`/dashboard/users-balances/${row.symbol}`)}
                        >
                            <TeamOutlined />
                        </button>
                    </Tooltip>
                ),
            },
        ],
        [navigate],
    );

    return (
        <Page className="pmx-users-balances" title="موجودی کاربران">
            {/* اسپینر فقط روی خود جدول است؛ blur روی Card باعث دو اسپینر هم‌زمان می‌شد. */}
            <Card title="موجودی کل کاربران به تفکیک ارز">
                <div className="table-toolbar">
                    <AntdInput.Search
                        className="symbol-search"
                        placeholder="جستجوی نماد ارز"
                        allowClear
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                    />
                    <div className="toolbar-actions">
                        <Button
                            className="reload-btn"
                            text="بارگذاری مجدد"
                            type={buttonType.outlined}
                            icon={<ReloadOutlined />}
                            onClick={loadData}
                        />
                        <Button
                            className="export-btn"
                            text="خروجی اکسل"
                            type={buttonType.info}
                            icon={<ExportOutlined />}
                            disabled={!filteredAssets.length}
                            onClick={() => downloadCSV('موجودی کاربران', filteredAssets)}
                        />
                    </div>
                </div>
                <div className="summary-bar">
                    <span>تعداد ارز: {digitSperator(filteredAssets.length)}</span>
                    <span className="en">
                        مجموع ارزش دلاری: {digitSperator(Number(totalDollarValue.toFixed(2)))} $
                    </span>
                </div>
                <AntdTable<UsersBalanceAsset>
                    className="users-balances-table"
                    rowKey="symbol"
                    columns={columns}
                    dataSource={filteredAssets}
                    loading={loading}
                    locale={{ emptyText: <Empty description="رکوردی یافت نشد" /> }}
                    scroll={{ x: 800 }}
                    pagination={{
                        current: page,
                        pageSize: pageSize,
                        showSizeChanger: true,
                        pageSizeOptions: [20, 50, 100, 1000, 10000, 100000],
                        onChange: (nextPage, nextPageSize) => {
                            setPage(nextPage);
                            setPageSize(nextPageSize);
                        },
                        showTotal: (total, range) =>
                            `نمایش ${digitSperator(range[0])} تا ${digitSperator(range[1])} از ${digitSperator(total)} رکورد`,
                    }}
                />
            </Card>
        </Page>
    );
};
